import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useClientBanner, useRecordVisit, useVisitContext,
} from '../api/queries.ts';
import { ApiError } from '../api/client.ts';
import { LoadFailure, isSessionFailure } from '../components/LoadFailure.tsx';
import { formatDate } from '../dates.ts';
import type { HealthFlag, VisitType } from '../api/types.ts';

const VISIT_TYPES: VisitType[] = [
  'Welfare Visit', 'Initial Site Visit', 'Emergency Visit',
  'Collateral Visit', 'Other',
];

const HEALTH_FLAGS: HealthFlag[] = [
  'None', 'Hospitalization', 'Significant Decline',
  'Injury', 'Behavioral Incident', 'Other',
];

/** Today, as a calendar day in the viewer's own zone — never via toISOString,
 *  which would roll over at 8pm in New York. */
function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 ' +
  'text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';

/**
 * Record a visit.
 *
 * Built for the place it is used: standing in a lobby, one-handed, on someone
 * else's wifi. Every control is at least 48px, the date defaults to today, and
 * the only required decisions are the date, the type, and where it happened.
 */
export function RecordVisit() {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const [visitDate, setVisitDate] = useState(todayLocal());
  const [visitType, setVisitType] = useState<VisitType>('Welfare Visit');
  const [locationChoice, setLocationChoice] =
    useState<'placement' | 'stay' | null>(null);
  const [minutes, setMinutes] = useState('');
  const [healthFlag, setHealthFlag] = useState<HealthFlag>('None');
  const [summary, setSummary] = useState('');

  const banner = useClientBanner(id);
  const where = useVisitContext(id, visitDate);
  const record = useRecordVisit(id);

  // A form for a client that could not be loaded is not a form. The header
  // used to say "Loading…" for ever.
  const sessionError = [banner.error, where.error].find(
    (e) => e !== null && isSessionFailure(e, { forbiddenIsSession: false }),
  );
  const pageError = sessionError ?? (banner.error && !banner.data ? banner.error : null);
  if (pageError) {
    return (
      <div className="mx-auto max-w-3xl bg-white min-h-dvh">
        <LoadFailure
          what="This client"
          error={pageError}
          onRetry={() => void banner.refetch()}
          retrying={banner.isFetching}
          forbiddenIsSession={false}
        />
      </div>
    );
  }

  // The server's suggestion until the case manager touches it. Resetting the
  // choice when the date changes is deliberate: a different date can mean a
  // different answer, and a stale selection would quietly outrank the new one.
  const suggested = where.data?.default_location ?? 'placement';
  const location = locationChoice ?? suggested;
  const stay = where.data?.stay ?? null;
  const placement = where.data?.placement ?? null;

  /**
   * One intention, one request.
   *
   * `disabled={record.isPending}` cannot stop a second click in the same tick:
   * disabling depends on a re-render that has not happened yet, so both
   * handlers run and both requests go. The database refused the second with a
   * 409 — correctly — but both calls share one mutation observer, so the
   * second call's error superseded the first call's success and its navigation
   * never ran. The case manager was left on the form, told the visit was
   * already recorded, about the visit they had just recorded.
   *
   * A ref is set synchronously, in the same tick as the first click, so the
   * second click never becomes a second request and there is no 409 to
   * misreport. It is released on a genuine failure, because a refusal must stay
   * retryable — someone else may have recorded that visit, and the date or the
   * type may need changing before trying again.
   */
  const sent = useRef(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (sent.current) return;
    sent.current = true;
    record.mutate(
      {
        client_id: id,
        visit_date: visitDate,
        visit_type: visitType,
        location,
        ...(minutes ? { duration_minutes: Number(minutes) } : {}),
        ...(healthFlag !== 'None' ? { health_flag: healthFlag } : {}),
        ...(summary.trim() ? { summary: summary.trim() } : {}),
      },
      {
        // `replace`, so the submitted form is not left in the history. Pushing
        // meant back from the client's record returned to a form that had
        // already been saved — and re-submitting it would record the visit
        // twice. The state is what the record says happened when it arrives.
        onSuccess: () => navigate(`/clients/${id}`, {
          replace: true,
          state: { recorded: 'Visit recorded.' },
        }),
        onError: () => { sent.current = false; },
      },
    );
  };

  return (
    <div className="mx-auto max-w-3xl bg-white min-h-dvh">
      <header className="sticky top-0 z-10 border-b border-neutral-200 bg-white px-4 py-3">
        <p className="text-xs font-bold tracking-[0.14em] text-neutral-500 uppercase">
          Record a visit
        </p>
        <h1 className="mt-0.5 text-xl leading-tight font-bold text-neutral-900">
          {banner.data?.display_name ?? 'Loading…'}
        </h1>
      </header>

      <form onSubmit={submit} className="space-y-5 px-4 py-4">
        <div>
          <label className={label} htmlFor="visit-date">Date of visit</label>
          <input
            id="visit-date" type="date" required
            value={visitDate} max={todayLocal()}
            onChange={(e) => { setVisitDate(e.target.value); setLocationChoice(null); }}
            className={field}
          />
        </div>

        <div>
          <label className={label} htmlFor="visit-type">Type</label>
          <select
            id="visit-type" value={visitType}
            onChange={(e) => setVisitType(e.target.value as VisitType)}
            className={field}
          >
            {VISIT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {/*
          Where it happened.
          Only shown when there is a genuine choice — a stay covering that date.
          Otherwise the answer is the placement and asking would be noise. When
          the choice exists the stay is preselected, because that is where the
          client was, and the alternative is offered plainly rather than buried:
          a case manager may have visited the empty home to meet a contractor,
          and that is a real visit to a real place.
        */}
        {stay ? (
          <fieldset>
            <legend className={label}>Where did the visit take place?</legend>
            <p className="mt-1 text-sm text-neutral-600">
              Away from their placement since {formatDate(stay.started_on)}
              {stay.bed_held && ' — bed held'}.
            </p>

            <div className="mt-2 space-y-2">
              {([
                ['stay', stay.location ?? stay.stay_type,
                 `${stay.stay_type}${stay.reason ? ` · ${stay.reason}` : ''}`],
                ['placement', placement?.location ?? 'Their placement',
                 placement?.kind_label ?? 'Where they live'],
              ] as const).map(([value, title, sub]) => (
                <label
                  key={value}
                  className={
                    'flex min-h-[60px] w-full items-start gap-3 rounded-lg border px-4 py-3 ' +
                    (location === value
                      ? 'border-neutral-900 bg-neutral-50'
                      : 'border-neutral-300 bg-white')
                  }
                >
                  <input
                    type="radio" name="location" value={value}
                    checked={location === value}
                    onChange={() => setLocationChoice(value)}
                    className="mt-1 size-5 shrink-0"
                  />
                  <span className="min-w-0">
                    <span className="block font-semibold text-neutral-900">{title}</span>
                    <span className="block text-sm text-neutral-600">{sub}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        ) : where.error && !where.data ? (
          /*
            A failed lookup is not "no stay". Treating it as one recorded the
            visit at the placement, silently, for a client who may have been in
            hospital that day. So the form says it does not know, and waits.
          */
          <div role="alert" className="rounded-lg border-l-8 border-warning-edge bg-warning-bg
                                       px-4 py-3 text-warning-fg">
            <p className="font-semibold">Could not check where they were on this date.</p>
            <p className="mt-1 text-[0.9375rem]">
              The visit cannot be saved until it is known, or it could be recorded
              against the wrong place.
            </p>
            <button
              type="button"
              disabled={where.isFetching}
              onClick={() => void where.refetch()}
              className="mt-3 min-h-[44px] rounded-lg border border-warning-edge px-4 font-semibold
                         disabled:opacity-50"
            >
              {where.isFetching ? 'Trying again…' : 'Try again'}
            </button>
          </div>
        ) : (
          placement && (
            <p className="rounded-lg bg-neutral-50 px-4 py-3 text-[0.9375rem] text-neutral-700">
              At <span className="font-semibold">{placement.location}</span>
              {placement.kind_label && ` · ${placement.kind_label}`}
            </p>
          )
        )}

        <div>
          <label className={label} htmlFor="minutes">Minutes (optional)</label>
          <input
            id="minutes" type="number" inputMode="numeric" min={1} max={1440}
            value={minutes} onChange={(e) => setMinutes(e.target.value)}
            className={field}
          />
        </div>

        <div>
          <label className={label} htmlFor="health">Anything of concern</label>
          <select
            id="health" value={healthFlag}
            onChange={(e) => setHealthFlag(e.target.value as HealthFlag)}
            className={field}
          >
            {HEALTH_FLAGS.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>

        <div>
          <label className={label} htmlFor="summary">What happened</label>
          <textarea
            id="summary" rows={6} value={summary}
            onChange={(e) => setSummary(e.target.value)}
            className={field + ' py-2 leading-snug'}
          />
        </div>

        {record.error && (
          <div className="rounded-lg border-l-8 border-critical-edge bg-critical-bg
                          px-4 py-3 text-critical-fg">
            <p className="font-semibold">{record.error.message}</p>
            {record.error instanceof ApiError && record.error.hint && (
              <p className="mt-1 text-[0.9375rem] opacity-95">{record.error.hint}</p>
            )}
          </div>
        )}

        <div className="flex gap-3 pb-8">
          <button
            type="submit" disabled={record.isPending || !where.data}
            className="min-h-[52px] flex-1 rounded-lg bg-neutral-900 px-4 font-semibold
                       text-white active:bg-neutral-700 disabled:opacity-50"
          >
            {record.isPending ? 'Saving…'
              : !where.data && !where.error ? 'Checking where they were…'
              : 'Record visit'}
          </button>
          <button
            type="button" onClick={() => navigate(`/clients/${id}`)}
            className="min-h-[52px] rounded-lg border border-neutral-300 px-4
                       font-semibold text-neutral-700 active:bg-neutral-100"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
